import React from 'react';
import { Link } from 'react-router-dom';

export default function ShelflinePrivacyPolicy() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-lg font-mono text-zinc-400 hover:text-white transition-colors">
            ← Back to Portfolio
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-zinc-400 text-lg">Shelfline - Board Game Collection & Play Tracker</p>
            <p className="text-zinc-500 text-sm mt-2">Last updated: July 14, 2026</p>
          </div>

          <div className="space-y-10 text-zinc-300 leading-relaxed">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Introduction</h2>
              <p>
                Welcome to Shelfline ("we," "our," or "us"). Shelfline is a local-first app for
                cataloging your board game collection, deciding what to play, and logging your
                plays. This Privacy Policy explains how information is handled when you use our
                mobile application ("App"). The short version: your data lives on your device,
                and we like it that way.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Information We Collect</h2>
              <p className="mb-4">
                <strong className="text-white">We do not collect any personal information.</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>We do not require you to create an account</li>
                <li>We do not collect your name, email address, or any contact information</li>
                <li>We do not collect payment information (your purchase is handled entirely by the App Store)</li>
                <li>We do not track your location</li>
                <li>We do not use cookies or similar tracking technologies</li>
                <li>We do not collect device identifiers or usage analytics</li>
              </ul>
            </section>

            {/* Your Data Stays on Your Device */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Your Data Stays on Your Device</h2>
              <p>
                Everything you create in Shelfline — your game collection, ratings, notes, play
                history, player profiles, and any photos you attach to plays — is stored locally
                on your device in the App's private database. We do not have access to this data,
                and it is never transmitted to our servers. The App works fully offline. If you
                delete the App, this data is removed from your device, so we recommend using the
                built-in JSON export (Settings → Your data) to keep your own backup.
              </p>
            </section>

            {/* Camera & Barcode Scanning */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Camera &amp; Barcode Scanning</h2>
              <p className="mb-4">
                Shelfline uses your device's camera to scan the barcodes on board game boxes. The
                camera feed is processed entirely on your device — no photos or video are ever
                recorded, stored, or uploaded by the scanning feature.
              </p>
              <p>
                When a scanned barcode isn't already recognized by the App, the numeric barcode
                digits (and nothing else) are sent to third-party product databases to identify
                the game. This request contains no personal information and is not linked to you.
              </p>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Third-Party Services</h2>
              <p className="mb-4">
                To identify an unrecognized barcode, the App may query the following public
                product databases with the barcode digits only:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <a
                    href="https://www.upcitemdb.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-400 hover:text-emerald-300 underline"
                  >
                    UPCitemdb
                  </a>
                </li>
                <li>
                  <a
                    href="https://world.openproductsfacts.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-400 hover:text-emerald-300 underline"
                  >
                    Open Products Facts
                  </a>
                </li>
              </ul>
              <p className="mt-4">
                The App may also contact the Shelfline game catalog service to look up games and
                barcodes and to contribute anonymous barcode-to-game matches that help other
                players' scans succeed. These requests contain only the barcode digits and the
                matched game — never your identity, your collection, or your play history.
              </p>
            </section>

            {/* Photos */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Photos</h2>
              <p>
                If you choose to attach a photo to a logged play, the App asks for access to your
                photo library. The selected photo is copied into the App's local storage on your
                device only. Photos are never uploaded anywhere.
              </p>
            </section>

            {/* Data Export */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Export</h2>
              <p>
                You can export all of your Shelfline data as a JSON file at any time from
                Settings. Exports are created on your device and shared only where you choose to
                send them (e.g., your own cloud storage or email). Once you share an export, its
                privacy is governed by the service you shared it to.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Children's Privacy</h2>
              <p>
                Shelfline does not collect personal information from anyone, including children
                under the age of 13.
              </p>
            </section>

            {/* Changes to This Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. If we make changes, we will
                notify you by revising the "Last updated" date at the top of this policy. We
                encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our privacy practices,
                please contact us at:
              </p>
              <p className="mt-4">
                <a
                  href="mailto:parkerdevwork@gmail.com"
                  className="text-emerald-400 hover:text-emerald-300 underline"
                >
                  parkerdevwork@gmail.com
                </a>
              </p>
            </section>

            {/* Summary */}
            <section className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-6 mt-12">
              <h2 className="text-xl font-semibold text-white mb-4">Summary</h2>
              <p className="text-zinc-400">
                In short: We don't collect your data. We don't require accounts. We don't track
                you. Your collection, plays, and photos live only on your device. The only thing
                that ever leaves your phone is the digits of a barcode we're trying to identify —
                and those aren't linked to you in any way.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center text-sm text-zinc-600">
          <p>© 2026 Shelfline. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
